import React from 'react';
import { Box, Button, Typography, Divider } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import ScaleResultEsteII from './ScaleResultESTE_II';
import ScaleResultEsteR from './ScaleResultESTE_R';
import ScaleResultFUMAT from './ScaleResultFUMAT';
import ScaleResultGDS from './ScaleResultGDS';
import ScaleResultMMSE from './ScaleResultMMSE';
import ScaleResultABVD from './ScaleResultABVD';
import ScaleResultELB from './ScaleResultELB';
import ScaleResultET from './ScaleResultET';
import ScaleResultVGI from './ScaleResultVGI';
import ScaleResultLIK from './ScaleResultLIK';
import ScaleResultISAACS from './ScaleResultISAACS';
import ScaleResultEAH from './ScaleResultEAH';
import ScaleResultRUD from './ScaleResultRUD';
import ScaleResultZARIT from './ScaleResultZARIT';
import ScaleResultIDB from './ScaleResultIDB';
import ScaleResultSPMSQ from './ScaleResultSPMSQ';
import { exportElementToPdf } from '../../pdf/htmlToPdf';
import VeraLogo from '../../../assets/image/VERA.png';
import { useRedirect } from 'react-admin';


export const ScaleResult = (props) => {
    const { onComputedResult } = props;
    const scaleCode = (props?.data?.code || props?.register?.scaleCode || '').toUpperCase();
    const title = props?.data?.name || props?.register?.scaleName || 'Resultados';
    const resultData = props?.register?.scaleResult
        || props?.register?.result
        || props?.data?.scaleResult
        || props?.data?.result
        || {};

    const register = props?.register;
    const resultRef = React.useRef(null);
    const redirect = useRedirect();

    const formatDate = React.useCallback((value) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }, []);

    const cleanNotes = (html) => {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || "";
    };

    // Sincronización: Extraemos la nota directamente para que useMemo reaccione al cambio de props.register
    const reportInfo = React.useMemo(() => {
        const patientName = register?.clientName || register?.userName || 'Paciente';
        const evaluationDate = formatDate(register?.createdDate);
        const professional = register?.professionalName || '';
        const scaleName = props?.data?.name || register?.scaleName || title || 'Escala';
        
        // Importante: Referenciamos register.scaleNotes aquí para que el memo se invalide al cambiar
        const scaleNotes = cleanNotes(register?.scaleNotes);

        return {
            patientName,
            ageLabel: register?.clientAge ? `${register.clientAge} años` : '',
            contract: register?.contractName || '',
            evaluationDate,
            professional,
            scaleName,
            scaleNotes 
        };
    }, [register, props?.data?.name, title, formatDate]); // Dependencia total del objeto register

    const normalizeSegment = React.useCallback((value, fallback) => {
        const base = (value || fallback || '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();

        return base
            .replace(/\s+/g, '_')
            .replace(/[^A-Za-z0-9_-]/g, '');
    }, []);

  const handleDownloadReport = React.useCallback(async () => {
    if (!resultRef.current) return;

    const wrapper = document.createElement('div');
    // MÁRGENES Y FORMATO ORIGINAL RECUPERADOS
    wrapper.style.width = '210mm';
    wrapper.style.minHeight = '297mm';
    wrapper.style.background = '#ffffff';
    wrapper.style.color = '#1d1d1b';
    wrapper.style.fontFamily = '"Helvetica Neue", Arial, sans-serif';
    wrapper.style.padding = '32px 36px 40px'; // Márgenes del PDF (19)
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.lineHeight = '1.45';
    wrapper.style.position = 'absolute';
    wrapper.style.left = '-99999px';
    wrapper.style.top = '0';
    wrapper.style.zIndex = '-1';

    const header = document.createElement('div');
    header.style.borderBottom = '2px solid #0e98a3';
    header.style.paddingBottom = '16px';

    const headerTop = document.createElement('div');
    headerTop.style.display = 'flex';
    headerTop.style.justifyContent = 'space-between';
    headerTop.style.alignItems = 'flex-start';
    headerTop.style.gap = '16px';

    const titleBlock = document.createElement('div');
    const subtitle = document.createElement('p');
    subtitle.textContent = 'Informe de Evaluación Sociosanitaria';
    subtitle.style.margin = '0';
    subtitle.style.color = '#0e98a3';
    subtitle.style.fontSize = '20px';
    subtitle.style.fontWeight = '600';

    const headerTitle = document.createElement('h1');
    headerTitle.textContent = `Escala: ${reportInfo.scaleName || ''}`;
    headerTitle.style.margin = '4px 0 0';
    headerTitle.style.fontSize = '24px';
    headerTitle.style.fontWeight = '700';

    titleBlock.appendChild(subtitle);
    titleBlock.appendChild(headerTitle);
    headerTop.appendChild(titleBlock);

    if (VeraLogo) {
        const logoImg = document.createElement('img');
        logoImg.src = VeraLogo;
        logoImg.style.width = '90px';
        logoImg.style.objectFit = 'contain';
        headerTop.appendChild(logoImg);
    }
    header.appendChild(headerTop);

    const infoBlock = document.createElement('div');
    infoBlock.style.marginTop = '12px';
    infoBlock.style.fontSize = '14px';

    const appendRow = (label, value) => {
        if (!value) return;
        const row = document.createElement('div');
        row.style.marginBottom = '4px';
        row.innerHTML = `<strong>${label}:</strong> <span>${value}</span>`;
        infoBlock.appendChild(row);
    };

    appendRow('Nombre', reportInfo.patientName);
    appendRow('Edad', reportInfo.ageLabel);
    appendRow('Contrato', reportInfo.contract);
    appendRow('Fecha de evaluación', reportInfo.evaluationDate);
    appendRow('Profesional evaluador', reportInfo.professional);

    header.appendChild(infoBlock);
    wrapper.appendChild(header);

    // 1. RESULTADOS (Cuerpo central)
    const resultClone = resultRef.current.cloneNode(true);
    resultClone.style.marginTop = '20px';
    wrapper.appendChild(resultClone);

    // 2. NOTAS AL FINAL (Debajo de los resultados)
    if (reportInfo.scaleNotes) {
        const notesSection = document.createElement('div');
        notesSection.style.marginTop = '25px';
        notesSection.style.padding = '12px';
        notesSection.style.backgroundColor = '#f4fbfb';
        notesSection.style.border = '1px solid #cce8ea';
        notesSection.style.borderRadius = '4px';

        const notesTitle = document.createElement('strong');
        notesTitle.textContent = 'Notas de evaluación:';
        notesTitle.style.display = 'block';
        notesTitle.style.marginBottom = '4px';
        notesTitle.style.fontSize = '14px';
        notesTitle.style.color = '#0e98a3';

        const notesText = document.createElement('p');
        notesText.textContent = reportInfo.scaleNotes;
        notesText.style.margin = '0';
        notesText.style.fontSize = '13px';
        notesText.style.whiteSpace = 'pre-wrap';

        notesSection.appendChild(notesTitle);
        notesSection.appendChild(notesText);
        wrapper.appendChild(notesSection);
    }

    document.body.appendChild(wrapper);
    const filename = `${normalizeSegment(reportInfo.patientName, 'Paciente')}_${normalizeSegment(reportInfo.scaleName, 'Escala')}.pdf`;

    try {
        await exportElementToPdf(wrapper, {
            filename,
            orientation: 'p',
            scale: 2,
            margins: { top: 10, right: 10, bottom: 10, left: 10 },
        });
    } finally {
        document.body.removeChild(wrapper);
    }
}, [normalizeSegment, reportInfo]);

    const renderResult = () => {
    // Definimos las props base que necesitan todos los componentes de resultados
    const commonProps = {
        title,           // Título de la escala
        register,        // El registro actual (con las respuestas)
        scale: props.data, // Definición de la escala (preguntas/factores)
        onComputedResult // Función para devolver el cálculo al padre
    };

    // Pasamos resultData (datos previos de DB) por separado para que el 
    // componente hijo decida si usar el previo o el calculado (derived)
    switch (scaleCode) {
        case 'ESTE_II': return <ScaleResultEsteII {...commonProps} factors={resultData.factors} totalScore={resultData.totalScore} />;
        case 'ESTE_R':  return <ScaleResultEsteR  {...commonProps} factors={resultData.factors} totalScore={resultData.totalScore} />;
        case 'FUMAT':   return <ScaleResultFUMAT   {...commonProps} dimensions={resultData.dimensions} totalScore={resultData.totalScore} />;
        case 'MMSE':    return <ScaleResultMMSE    {...commonProps} totalScore={resultData.totalScore} />;
        case 'GDS':     return <ScaleResultGDS     {...commonProps} totalScore={resultData.totalScore} />;
        case 'ELB':     return <ScaleResultELB     {...commonProps} totalScore={resultData.totalScore} />;
        case 'ABVD':    return <ScaleResultABVD    {...commonProps} totalScore={resultData.totalScore} />;
        case 'ET':      return <ScaleResultET      {...commonProps} totalScore={resultData.totalScore} />;
        case 'VGI':     return <ScaleResultVGI     {...commonProps} />;
        case 'LIK':     return <ScaleResultLIK     {...commonProps} categories={resultData.categories} />;
        case 'ISAACS':  return <ScaleResultISAACS  {...commonProps} totalScore={resultData.totalScore} />;
        case 'EAH':     return <ScaleResultEAH     {...commonProps} totalScore={resultData.totalScore} />;
        case 'RUD':     return <ScaleResultRUD     {...commonProps} />;
        case 'ZARIT':   return <ScaleResultZARIT   {...commonProps} totalScore={resultData.totalScore} />;
        case 'IDB':     return <ScaleResultIDB     {...commonProps} totalScore={resultData.totalScore} />;
        case 'SPMSQ':   return <ScaleResultSPMSQ   {...commonProps} totalScore={resultData.totalScore} />;
        default:        return <Typography variant="body1" style={{ marginBottom: 12 }}>{title}</Typography>;
    }
};

    return (
        <>
            <Divider style={{ marginBottom: 24, marginTop: 24 }} />
            <div ref={resultRef}>{renderResult()}</div>
            <Box className="sf-actions">
                <Box />
                <Box className="sf-action-buttons">
                    {scaleCode !== 'VGI' && (
                        <Button variant="contained" color="primary" onClick={handleDownloadReport} style={{ marginRight: 8 }} startIcon={<DescriptionIcon />}>
                            Descargar informe
                        </Button>
                    )}
                    <Button variant="outlined" onClick={() => props.handleScaleResult(false)} style={{ marginRight: 8 }}>Ver respuestas</Button>
                    <Button color="primary" variant="outlined" onClick={() => props.onCancel()} style={{ marginRight: 8 }}>Volver</Button>
                    <Button variant="outlined" onClick={() => props?.handleDialogScaleNote(true)} style={{ marginRight: 8 }}>Añadir Notas</Button>
                    <Button variant="outlined" onClick={() => redirect('/objectives?filter={"clientName":"' + (register?.clientName || '') + '"}' )} style={{ marginRight: 8 }}>Objetivos</Button>
                </Box>
            </Box>
        </>
    );
};

export default ScaleResult;
